import {
  myApplicationSchema,
  portalMeSchema,
  type MyApplication,
  type PortalMe,
} from '@growup/streaming-shared';
import { ButtonLink, Checkbox, Textarea } from '@growup/ui';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { getJson, messageOf, send } from './api';
import { formatDate } from './format';
import { siteLink, usePortal } from './portal';
import { Button, Card, ErrorNote, Field, Notice, PageTitle, TextInput } from './ui';
import { useRunner } from './useRunner';

const RULES = [
  'Represento a Grow uP com respeito: sem discurso de ódio, assédio ou conteúdo para adultos nas transmissões com a marca.',
  'Nas transmissões de eventos da Grow uP sigo as indicações da equipa do evento.',
  'Os números do meu canal (horas em direto, espectadores, seguidores) são lidos da Twitch e vistos pela equipa de streaming.',
];

/** `/candidatura`: apply, follow the application, or withdraw it. */
export function Apply() {
  const [me, setMe] = useState<PortalMe | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    getJson('/api/me', portalMeSchema)
      .then(setMe)
      .catch((e: unknown) => setError(messageOf(e)));
  }, []);
  useEffect(load, [load]);

  return (
    <div className="flex max-w-2xl flex-col gap-6">
      <PageTitle lead="Junta-te à equipa de streamers da Grow uP. A partir dos 18 anos.">
        Candidatura
      </PageTitle>
      <ErrorNote message={error} />
      {me === null ? null : me.streamer ? (
        <Notice title="Já és streamer da Grow uP">
          <span className="flex flex-col gap-3">
            <span>O teu canal twitch.tv/{me.streamer.channel} faz parte da equipa.</span>
            <ButtonLink href="/painel" size="sm" className="self-start">
              Ir para o painel
            </ButtonLink>
          </span>
        </Notice>
      ) : me.application?.status === 'pending' ? (
        <Pending application={me.application} onWithdrawn={load} />
      ) : (
        <>
          {me.application?.status === 'rejected' ? (
            <Notice
              title={`A candidatura de ${formatDate(me.application.createdAt)} não foi aceite`}
            >
              {me.application.decisionNote
                ? `Nota da equipa: ${me.application.decisionNote}`
                : 'Podes candidatar-te de novo quando quiseres.'}
            </Notice>
          ) : null}
          <ApplicationForm me={me} onSent={load} />
        </>
      )}
    </div>
  );
}

function Pending({
  application,
  onWithdrawn,
}: {
  application: MyApplication;
  onWithdrawn: () => void;
}) {
  const { busy, error, run } = useRunner();
  return (
    <Card title="À espera de resposta">
      <div className="flex flex-col gap-4">
        <p className="text-body text-gu-ink-muted">
          Enviaste a candidatura a {formatDate(application.createdAt)}. A equipa de streaming
          responde por email.
        </p>
        <dl className="grid gap-3 text-body sm:grid-cols-[10rem_1fr]">
          <dt className="text-gu-ink-muted">Canal</dt>
          <dd>
            twitch.tv/{application.channel}
            {application.twitchVerified ? ' (verificado)' : ''}
          </dd>
          <dt className="text-gu-ink-muted">O que transmites</dt>
          <dd className="whitespace-pre-line">{application.content}</dd>
          <dt className="text-gu-ink-muted">Quando</dt>
          <dd className="whitespace-pre-line">{application.schedule}</dd>
        </dl>
        <ErrorNote message={error} />
        <Button
          tone="quiet"
          className="self-start"
          disabled={busy}
          onClick={() =>
            void run(async () => {
              await send('DELETE', '/api/me/application', undefined, myApplicationSchema);
              onWithdrawn();
            })
          }
        >
          Desistir da candidatura
        </Button>
      </div>
    </Card>
  );
}

function ApplicationForm({ me, onSent }: { me: PortalMe; onSent: () => void }) {
  const portal = usePortal();
  const [channel, setChannel] = useState('');
  const [content, setContent] = useState('');
  const [schedule, setSchedule] = useState('');
  const [motivation, setMotivation] = useState('');
  const [acceptRules, setAcceptRules] = useState(false);
  const { busy, error, fields, run } = useRunner();

  const needsLink = portal?.twitch === true && me.twitch === null;
  const social = siteLink(portal, '/conta/redes');

  return (
    <Card title="A tua candidatura">
      <form
        className="flex flex-col gap-5"
        onSubmit={(e) => {
          e.preventDefault();
          void run(async () => {
            await send(
              'POST',
              '/api/me/application',
              {
                channel: portal?.twitch ? null : channel.trim() || null,
                content,
                schedule,
                motivation,
                acceptRules,
              },
              myApplicationSchema,
            );
            onSent();
          });
        }}
      >
        {portal?.twitch ? (
          me.twitch ? (
            <p className="text-body">
              Canal: <strong>twitch.tv/{me.twitch.login}</strong>, ligado à tua conta Grow uP.
            </p>
          ) : (
            <Notice title="Liga primeiro a tua Twitch">
              <span className="flex flex-col gap-2">
                <span>
                  A candidatura usa o canal ligado à tua conta Grow uP, para termos a certeza de que
                  é teu. Liga-o no site, em Redes, e volta a esta página.
                </span>
                {social ? (
                  <a className="gu-link self-start" href={social}>
                    Ligar a Twitch no site
                  </a>
                ) : null}
              </span>
            </Notice>
          )
        ) : (
          <Field
            label="Canal da Twitch"
            hint="Como aparece em twitch.tv/<canal>."
            error={fields.channel}
          >
            <TextInput
              autoComplete="off"
              value={channel}
              onChange={(e) => setChannel(e.target.value)}
            />
          </Field>
        )}
        <Field
          label="O que transmites"
          hint="Jogos, tipo de conteúdo, em que língua."
          error={fields.content}
        >
          <Textarea rows={3} value={content} onChange={(e) => setContent(e.target.value)} />
        </Field>
        <Field
          label="Quando costumas transmitir"
          hint="Por exemplo: terças e quintas à noite."
          error={fields.schedule}
        >
          <TextInput value={schedule} onChange={(e) => setSchedule(e.target.value)} />
        </Field>
        <Field label="Porque queres ser streamer da Grow uP" error={fields.motivation}>
          <Textarea rows={5} value={motivation} onChange={(e) => setMotivation(e.target.value)} />
        </Field>
        <div className="flex flex-col gap-2">
          <p className="font-sub text-label font-bold uppercase text-gu-ink">Regras</p>
          <ul className="list-disc pl-5 text-body text-gu-ink-muted">
            {RULES.map((r) => (
              <li key={r}>{r}</li>
            ))}
          </ul>
          <Checkbox
            label="Li e aceito as regras dos streamers da Grow uP"
            checked={acceptRules}
            onChange={(e) => setAcceptRules(e.target.checked)}
          />
          {fields.acceptRules ? (
            <span className="text-caption text-gu-danger">{fields.acceptRules}</span>
          ) : null}
        </div>
        <ErrorNote message={error} />
        <Button tone="primary" type="submit" className="self-start" disabled={busy || needsLink}>
          Enviar candidatura
        </Button>
        <p className="text-caption text-gu-ink-muted">
          Entraste como {me.login}. <Link to="/">Voltar ao início</Link>
        </p>
      </form>
    </Card>
  );
}
